import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.2 Tier 4 — index page for EVA snapshots. Handles:
//   * "Compare" button opens the right-side picker modal
//   * Delete action confirms via a styled modal (no browser confirm)
//   * Navigation to the compare view
//
// Capture lives on eva_controller.js (EVA page header button).
export default class extends Controller {
  static targets = [
    "compareModal", "compareLeftLabel", "compareRight",
    "deleteModal", "deleteSnapshotLabel"
  ]

  static values = {
    projectId: Number,
    budgetId: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._leftSnapshotId = null
    this._pendingDeleteId = null
  }

  showCompareModal(event) {
    const { snapshotIdParam, snapshotLabelParam } = event.currentTarget.dataset
    this._leftSnapshotId = parseInt(snapshotIdParam, 10)
    if (this.hasCompareLeftLabelTarget) {
      this.compareLeftLabelTarget.textContent = snapshotLabelParam || `Snapshot #${this._leftSnapshotId}`
    }
    this._show(this.compareModalTarget)
  }

  hideCompareModal() { this._hide(this.compareModalTarget) }

  runCompare() {
    if (!this._leftSnapshotId) return
    const right = this.compareRightTarget.value
    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/eva_snapshots/compare?left=${this._leftSnapshotId}&right=${encodeURIComponent(right)}`
    this._track("EVA Snapshot Compared", { left: this._leftSnapshotId, right })
    window.location.href = url
  }

  async deleteSnapshot(event) {
    const { snapshotIdParam, snapshotLabelParam } = event.currentTarget.dataset
    const id = parseInt(snapshotIdParam, 10)
    const label = snapshotLabelParam || `Snapshot #${id}`

    if (!window.confirmStyledDelete) {
      // Use a minimal inline confirm modal instead of browser confirm —
      // respects CLAUDE.md convention. Dynamically built to avoid bloating
      // the index view with a delete-modal target that's rarely used.
      const ok = await this._inlineConfirm(`Delete ${label}? This cannot be undone.`)
      if (!ok) return
    }

    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/eva_snapshots/${id}`
    const resp = await fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    })
    if (!resp.ok) {
      this._showToast("Failed to delete snapshot", "error")
      return
    }
    this._track("EVA Snapshot Deleted", { id })
    const row = this.element.querySelector(`tr[data-snapshot-id='${id}']`)
    if (row) row.remove()
    this._showToast("Snapshot deleted")
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Helpers ───────────────────────────────────────────────────────

  _show(el) { if (el) el.classList.remove("hidden") }
  _hide(el) { if (el) el.classList.add("hidden") }

  async _inlineConfirm(message) {
    return new Promise(resolve => {
      const backdrop = document.createElement("div")
      backdrop.className = "fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
      backdrop.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4" data-inline-panel>
          <div class="px-4 py-3 border-b border-gray-200">
            <h5 class="text-base font-semibold text-gray-800">Delete snapshot?</h5>
          </div>
          <div class="px-4 py-3 text-[13px] text-gray-700" data-msg></div>
          <div class="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
            <button type="button" data-cancel class="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-[13px] cursor-pointer">Cancel</button>
            <button type="button" data-confirm class="px-3 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 text-[13px] font-medium cursor-pointer">Delete</button>
          </div>
        </div>
      `
      backdrop.querySelector("[data-msg]").textContent = message
      document.body.appendChild(backdrop)
      const cleanup = result => {
        document.body.removeChild(backdrop)
        resolve(result)
      }
      backdrop.querySelector("[data-cancel]").addEventListener("click", () => cleanup(false))
      backdrop.querySelector("[data-confirm]").addEventListener("click", () => cleanup(true))
      backdrop.addEventListener("click", e => { if (e.target === backdrop) cleanup(false) })
      const panel = backdrop.querySelector("[data-inline-panel]")
      panel.addEventListener("click", e => e.stopPropagation())
    })
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }
}
